import React, { useState, useEffect, useRef } from "react";
import "./AnimationPopup.css";

const Preview = ({
  style,
  keyframes,
  isPlaying,
  timelinePosition,
  children,
}) => {
  const previewRef = useRef(null);

  useEffect(() => {
    if (previewRef.current) {
      const animation = previewRef.current.animate(keyframes, {
        duration: parseFloat(style.animationDuration) * 1000 || 1000,
        delay: parseFloat(style.animationDelay) * 1000 || 0,
        iterations:
          style.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(style.animationIterationCount) || 1,
        direction: style.animationDirection || "normal",
        fill: style.animationFillMode || "none",
        easing: style.animationTimingFunction || "linear",
      });

      if (isPlaying) {
        animation.currentTime =
          (timelinePosition / 100) * animation.effect.getTiming().duration;
        animation.play();
      } else {
        animation.pause();
        animation.currentTime =
          (timelinePosition / 100) * animation.effect.getTiming().duration;
      }

      return () => animation.cancel();
    }
  }, [style, keyframes, isPlaying, timelinePosition]);

  return (
    <div ref={previewRef} className="animation-preview" style={style}>
      {children}
    </div>
  );
};

const AnimationPopup = ({ isOpen, onClose, selectedDiv, handleChange }) => {
  const [previewAnimation, setPreviewAnimation] = useState({});
  const [timelinePosition, setTimelinePosition] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [keyframes, setKeyframes] = useState([]);
  const [selectedKeyframe, setSelectedKeyframe] = useState(null);
  const animationRef = useRef(null);

  const renderMutableInput = (label, name, array) => {
    return (
      <div key={name} style={styles.inputGroup}>
        <label style={styles.label}>{label}: </label>
        <div style={styles.widthHeightInputGroup}>
          <select
            name={name}
            value={selectedDiv ? selectedDiv[name] : ""}
            onChange={(e) => handlePropertyChange(name, e.target.value)}
            style={styles.select}
          >
            {array.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <input
            type="text"
            name={name}
            value={selectedDiv ? selectedDiv[name] : ""}
            onChange={(e) => handlePropertyChange(name, e.target.value)}
            style={styles.input}
          />
        </div>
      </div>
    );
  };
  const renderInput = (label, name, type = "text", additionalProps = {}) => {
    return (
      <div key={name} style={styles.inputGroup}>
        <label htmlFor={name} style={styles.label}>
          {label}:{" "}
        </label>
        <input
          type={type}
          name={name}
          value={selectedDiv[name] || (type === "color" ? "#ffffff" : "")}
          onChange={(e) => handlePropertyChange(name, e.target.value)}
          style={styles.input}
          placeholder={label}
          {...additionalProps}
        />
      </div>
    );
  };
  const renderSelect = (label, name, options) => {
    return (
      <div key={name} style={styles.inputGroup}>
        <label htmlFor={name} style={styles.label}>
          {label}:{" "}
        </label>
        <select
          name={name}
          value={selectedDiv[name] || ""}
          onChange={(e) => handlePropertyChange(name, e.target.value)}
          style={styles.select}
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  };

  useEffect(() => {
    if (selectedDiv && selectedDiv.keyframes) {
      setKeyframes(selectedDiv.keyframes);
    } else {
      setKeyframes([
        { position: 0, properties: {} },
        { position: 100, properties: {} },
      ]);
    }
  }, [selectedDiv]);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      cancelAnimationFrame(animationRef.current);
    }
    return () => cancelAnimationFrame(animationRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);
  const animate = (timestamp) => {
    const duration = parseFloat(selectedDiv.animationDuration) * 1000 || 1000;
    const progress = ((timestamp % duration) / duration) * 100;
    setTimelinePosition(progress);
    updatePreviewAnimation(progress);
    animationRef.current = requestAnimationFrame(animate);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      setTimelinePosition(0);
    }
  };

  const handleTimelineChange = (e) => {
    const newPosition = Number(e.target.value);
    setTimelinePosition(newPosition);
    setIsPlaying(false);
    updatePreviewAnimation(newPosition);
  };

  const updatePreviewAnimation = (position) => {
    if (keyframes.length === 0) return;

    const currentKeyframe = keyframes.reduce(
      (prev, curr) => (curr.position <= position ? curr : prev),
      keyframes[0]
    );

    setPreviewAnimation(currentKeyframe.properties);
    setSelectedKeyframe(currentKeyframe);
  };

  const handlePropertyChange = (name, value) => {
    setPreviewAnimation((prev) => ({ ...prev, [name]: value }));
    handleChange({ target: { name, value } });

    if (selectedKeyframe) {
      const updatedKeyframes = keyframes.map((kf, index) =>
        kf.position === selectedKeyframe.position
          ? { ...kf, properties: { ...kf.properties, [name]: value } }
          : kf
      );
      setKeyframes(updatedKeyframes);
      updateAnimation(updatedKeyframes);
    }
  };

  const handleAddKeyframe = () => {
    const newKeyframe = {
      position: timelinePosition,
      properties: { ...previewAnimation },
    };
    const updatedKeyframes = [...keyframes, newKeyframe].sort(
      (a, b) => a.position - b.position
    );
    setKeyframes(updatedKeyframes);
    setSelectedKeyframe(newKeyframe);
    updateAnimation(updatedKeyframes);
  };

  const handleDeleteKeyframe = () => {
    if (selectedKeyframe && keyframes.length > 2) {
      const updatedKeyframes = keyframes.filter(
        (kf) => kf.position !== selectedKeyframe.position
      );
      setKeyframes(updatedKeyframes);
      setSelectedKeyframe(null);
      updateAnimation(updatedKeyframes);
    }
  };

  const handleSelectKeyframe = (kf) => {
    setSelectedKeyframe(kf);
    setTimelinePosition(kf.position);
    setPreviewAnimation(kf.properties);
  };

  const updateAnimation = (updatedKeyframes) => {
    const animationName = `animation_${selectedDiv.id}`;
    const keyframeCSS = generateKeyframeCSS(animationName, updatedKeyframes);
    handleChange({ target: { name: "animationName", value: animationName } });
    handleChange({ target: { name: "animationDuration", value: "3s" } });
    handleChange({ target: { name: "keyframeCSS", value: keyframeCSS } });
    handleChange({ target: { name: "keyframes", value: updatedKeyframes } });
  };

  const kebabCase = (str) => {
    return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
  };
  const generateKeyframeCSS = (animationName, kframes) => {
    const excludedProps = [
      "animationFillMode",
      "animationDirection",
      "animationInfinite",
      "animationIterationCount",
      "animationDelay",
      "animationTimingFunction",
      "animationDuration",
    ];

    const mergedKeyframes = kframes.reduce((acc, kf) => {
      const existingKF = acc.find((item) => item.position === kf.position);
      if (existingKF) {
        existingKF.properties = { ...existingKF.properties, ...kf.properties };
      } else {
        acc.push({ ...kf });
      }
      return acc;
    }, []);

    let css = `@keyframes ${animationName} {\n`;
    mergedKeyframes.forEach((kf) => {
      let transform = "";
      css += `  ${kf.position}% {\n`;
      Object.entries(kf.properties).forEach(([prop, value]) => {
        if (!excludedProps.includes(prop)) {
          if (prop.startsWith("KEY")) {
            prop = kebabCase(prop.replace("KEY", ""));
          }
          if (
            prop === "scale" ||
            prop === "translateX" ||
            prop === "translateY" ||
            prop === "rotate" ||
            prop === "skewX" ||
            prop === "skewY"
          ) {
            if (prop === "scale") {
              if (value) {
                transform += `scale(${value}) `;
              }
            } else if (prop === "translateX") {
              if (value) {
                transform += `translateX(${value}) `;
              }
            } else if (prop === "translateY") {
              if (value) {
                transform += `translateY(${value}) `;
              }
            } else if (prop === "rotate") {
              if (value) {
                transform += `rotate(${value}) `;
              }
            } else if (prop === "skewX") {
              if (value) {
                transform += `skewX(${value}) `;
              }
            } else if (prop === "skewY") {
              if (value) {
                transform += `skewY(${value}) `;
              }
            }
          } else {
            css += `    ${prop}: ${value};\n`;
          }
        }
      });
      if (transform) {
        css += `    transform: ${transform.trim()};\n`;
      }
      css += "  }\n";
    });
    css += "}";
    return css;
  };

  if (!isOpen) return null;

  const generateKeyframes = () => {
    return keyframes.map((kf) => ({
      offset: kf.position / 100,
      ...getComputedStyle(kf.properties),
    }));
  };
  const getComputedStyle = (properties) => {
    const style = { ...properties };
    let transform = "";

    Object.keys(style).forEach((prop) => {
      if (prop.startsWith("KEY")) {
        const newProp = kebabCase(prop.replace("KEY", ""));
        style[newProp] = style[prop];
        delete style[prop];
      }
    });

    if (style.scale) transform += `scale(${style.scale}) `;
    if (style.translateX) transform += `translateX(${style.translateX}) `;
    if (style.translateY) transform += `translateY(${style.translateY}) `;
    if (style.rotate) transform += `rotate(${style.rotate}) `;
    if (style.skewX) transform += `skewX(${style.skewX}) `;
    if (style.skewY) transform += `skewY(${style.skewY}) `;
    if (transform) style.transform = transform.trim();

    ["scale", "translateX", "translateY", "rotate", "skewX", "skewY"].forEach(
      (prop) => delete style[prop]
    );

    return style;
  };
  const combinedStyle = {
    ...selectedDiv,
    ...getComputedStyle(selectedDiv),
    ...getComputedStyle(previewAnimation),
  };

  const styles = {
    container: {
      padding: "5px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      backgroundColor: "#f9f9f9",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      maxWidth: "400px",
      margin: "20px auto",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      overflow: "hidden",
    },
    heading: {
      fontSize: "15px",
      marginBottom: "10px",
      color: "#333",
      borderBottom: "1px solid #ddd",
      paddingBottom: "5px",
    },
    inputGroup: {
      marginBottom: "10px",
    },
    label: {
      display: "block",
      marginBottom: "5px",
      fontWeight: "bold",
      color: "#555",
    },
    input: {
      width: "100%",
      padding: "8px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "8px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      boxSizing: "border-box",
    },
  };

  return (
    <div className="animation-popup">
      <div className="animation-popup__content">
        <div className="animation-popup__close-buttondiv">
          <button className="animation-popup__close-button" onClick={onClose}>
            X
          </button>
        </div>
        <div className="animation_preview_timeline_buttons">
          <div className="animation-popup__preview">
            <Preview
              style={combinedStyle}
              keyframes={generateKeyframes()}
              isPlaying={isPlaying}
              timelinePosition={timelinePosition}
            >
              {(() => {
                let content = "";
                switch (selectedDiv.type) {
                  case "DroppableButton":
                    content = selectedDiv.label || "button";
                    break;
                  case "DroppableHeading":
                    content = selectedDiv.text || "Heading";
                    break;
                  case "DroppableParagraph":
                    content = selectedDiv.text || "Paragraph";
                    break;
                  default:
                    content = "Default Content";
                    break;
                }
                return content;
              })()}
            </Preview>
          </div>
          <div className="animation-popup__timeline">
            <div className="animation-popup__timeline-wrapper">
              <div className="animation-popup__timeline-ticks">
                {[...Array(11)].map((_, i) => (
                  <div key={i} className="animation-popup__timeline-tick">
                    <span>{i * 10}%</span>
                  </div>
                ))}
              </div>
              <div className="animation-popup__timeline-markers">
                {keyframes.map((kf, index) => (
                  <div
                    key={`${kf.position}-${index}`}
                    className={`animation-popup__timeline-marker ${
                      selectedKeyframe &&
                      selectedKeyframe.position === kf.position
                        ? "selected"
                        : ""
                    }`}
                    style={{ left: `${kf.position}%` }}
                    onClick={() => handleSelectKeyframe(kf)}
                  />
                ))}
              </div>
              <div
                className="animation-popup__timeline-scrubber"
                style={{ left: `${timelinePosition}%` }}
              />
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={timelinePosition}
              onChange={handleTimelineChange}
              className="animation-popup__timeline-input"
            />
          </div>
          <div className="animation-popup__controls">
            <button onClick={handlePlayPause}>
              {isPlaying ? "Pause" : "Play"}
            </button>
            <button onClick={handleDeleteKeyframe} disabled={!selectedKeyframe}>
              Delete Keyframe
            </button>
            <button onClick={handleAddKeyframe}>Add Keyframe</button>
          </div>
        </div>

        <div className="animation-properties">
          <h3>Transform</h3>
          <div className="animation-properties_div">
            {/* <input
              type="text"
              name="transformOrigin"
              value={selectedDiv.transformOrigin || ""}
              onChange={(e) =>
                handlePropertyChange("transformOrigin", e.target.value)
              }
              placeholder="(e.g., center 50%)"
            /> */}
            {renderInput("Scale", "scale", "number")}
            {renderInput("Translate X", "translateX", "text", {
              placeholder: "(e.g., 10px, 20px)",
            })}
            {renderInput("Translate Y", "translateY", "text", {
              placeholder: "(e.g., 10px, 20px)",
            })}
            {renderInput("Rotate", "rotate", "text", {
              placeholder: "(e.g., 20deg, 10deg)",
            })}
            {renderInput("Skew X", "skewX", "text", {
              placeholder: "(e.g., 20deg)",
            })}
            {renderInput("Skew Y", "skewY", "text", {
              placeholder: "(e.g., 20deg)",
            })}
          </div>
          <h3>Design</h3>
          <div className="animation-properties_div">
            {renderInput("Width", "KEYwidth")}
            {renderInput("Height", "KEYheight")}
            {renderInput("Margin", "KEYmargin")}
            {renderInput("Margin Top", "KEYmarginTop")}
            {renderInput("Margin Right", "KEYmarginRight")}
            {renderInput("Margin Bottom", "KEYmarginBottom")}
            {renderInput("Margin Left", "KEYmarginLeft")}
            {renderInput("Padding", "KEYpadding")}
            {renderInput("Padding Top", "KEYpaddingTop")}
            {renderInput("Padding Right", "KEYpaddingRight")}
            {renderInput("Padding Bottom", "KEYpaddingBottom")}
            {renderInput("Padding Left", "KEYpaddingLeft")}

            {renderInput("Color", "KEYcolor", "color")}
            {renderInput("Background Color", "KEYbackgroundColor", "color")}
            {renderInput("Border", "KEYborder")}
            {renderInput("Border Radius", "KEYborderRadius")}
            {renderInput("Opacity", "KEYopacity", "number", {
              min: "0",
              max: "1",
              step: "0.1",
              placeholder: "Opacity",
            })}
            {renderInput("Font Size", "KEYfontSize", "text", {
              placeholder: "Font Size",
            })}
            {renderSelect("Font Family", "KEYfontFamily", [
              "Arial",
              "Helvetica",
              "Times New Roman",
              "Courier New",
              "Verdana",
              "Georgia",
              "Palatino",
              "Garamond",
              "Comic Sans MS",
              "Trebuchet MS",
              "Arial Black",
              "Impact",
            ])}
            {renderSelect("Font Weight", "KEYfontWeight", [
              "normal",
              "bold",
              "100",
              "200",
              "300",
              "400",
              "500",
              "600",
              "700",
              "800",
              "900",
            ])}

            {renderSelect("Font Style", "KEYfontStyle", [
              "normal",
              "italic",
              "oblique",
            ])}

            {renderSelect("Text Align", "KEYtextAlign", [
              "left",
              "center",
              "right",
              "justify",
            ])}

            {renderSelect("Text Decoration", "KEYtextDecoration", [
              "none",
              "underline",
              "overline",
              "line-through",
            ])}

            {renderSelect("Text Transform", "KEYtextTransform", [
              "none",
              "capitalize",
              "uppercase",
              "lowercase",
            ])}
            {renderInput("Outline", "KEYoutline", "text", {
              placeholder: "Outline",
            })}
            {renderInput("Box Shadow", "KEYboxShadow", "text", {
              placeholder: "Box Shadow",
            })}
            {renderMutableInput("Filter", "KEYfilter", [
              "none",
              "blur(5px)",
              "brightness(0.5)",
              "contrast(150%)",
              "drop-shadow(10px 10px 5px grey)",
              "grayscale(100%)",
              "hue-rotate(90deg)",
              "invert(100%)",
              "opacity(50%)",
              "saturate(200%)",
              "sepia(100%)",
            ])}

            {renderMutableInput("Back-Drop Filter", "KEYbackdropFilter", [
              "none",
              "blur(5px)",
              "brightness(0.5)",
              "contrast(150%)",
              "drop-shadow(10px 10px 5px grey)",
              "grayscale(100%)",
              "hue-rotate(90deg)",
              "invert(100%)",
              "opacity(50%)",
              "saturate(200%)",
              "sepia(100%)",
            ])}
          </div>
          <h3>Animation Control</h3>
          <div className="animation-properties_div">
            <label htmlFor="animationDuration">Animation Duration</label>
            <input
              type="text"
              name="animationDuration"
              value={selectedDiv.animationDuration || ""}
              onChange={(e) =>
                handlePropertyChange("animationDuration", e.target.value)
              }
              placeholder="Duration (e.g., 1s)"
            />
            <label htmlFor="animationDelay">Animation Delay</label>
            <input
              type="text"
              name="animationDelay"
              value={selectedDiv.animationDelay || ""}
              onChange={(e) =>
                handlePropertyChange("animationDelay", e.target.value)
              }
              placeholder="Delay (e.g., 0.5s)"
            />
            <label htmlFor="animationDirection">Animation Direction</label>
            <select
              name="animationDirection"
              value={selectedDiv.animationDirection || ""}
              onChange={(e) =>
                handlePropertyChange("animationDirection", e.target.value)
              }
            >
              <option value="">Select Direction</option>
              <option value="normal">Normal</option>
              <option value="reverse">Reverse</option>
              <option value="alternate">Alternate</option>
              <option value="alternate-reverse">Alternate Reverse</option>
            </select>
            <label htmlFor="animationFillMode">Animation Fill Mode</label>
            <select
              name="animationFillMode"
              value={selectedDiv.animationFillMode || ""}
              onChange={(e) =>
                handlePropertyChange("animationFillMode", e.target.value)
              }
            >
              <option value="">Select Fill Mode</option>
              <option value="none">None</option>
              <option value="forwards">Forwards</option>
              <option value="backwards">Backwards</option>
              <option value="both">Both</option>
            </select>
            <label htmlFor="animationTimingFunction">
              Animation Timing Functions
            </label>
            <select
              name="animationTimingFunction"
              value={selectedDiv.animationTimingFunction || ""}
              onChange={(e) =>
                handlePropertyChange("animationTimingFunction", e.target.value)
              }
            >
              <option value="">Select Timing Function</option>
              <option value="linear">Linear</option>
              <option value="ease">Ease</option>
              <option value="ease-in">Ease In</option>
              <option value="ease-out">Ease Out</option>
              <option value="ease-in-out">Ease In Out</option>
            </select>
            <label htmlFor="animationIterationCount">
              Animation Iteration Count
            </label>
            <input
              type="number"
              name="animationIterationCount"
              value={selectedDiv.animationIterationCount || ""}
              onChange={(e) =>
                handlePropertyChange("animationIterationCount", e.target.value)
              }
              placeholder="Iterations"
              disabled={selectedDiv.animationInfinite}
            />
            <label>
              <input
                type="checkbox"
                name="animationInfinite"
                checked={selectedDiv.animationInfinite || false}
                onChange={(e) => {
                  handlePropertyChange("animationInfinite", e.target.checked);
                  if (e.target.checked) {
                    handlePropertyChange("animationIterationCount", "infinite");
                  }
                }}
              />
              Infinite/Loop
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimationPopup;
